import { el } from '../ui.js';

/**
 * The public front page — the pitch, and the door to the sign-up form.
 *
 * A NOTE ON WHAT THIS PAGE CLAIMS, WHICH MATTERS MORE HERE THAN ANYWHERE ELSE
 * Strangers create accounts from this page. Everything written in the present
 * tense is something the software does today. Anything still being built is
 * written in the future tense and carries a visible "in development" marker.
 * Selling an insurance agency a capability that does not exist is not a
 * marketing risk, it is a legal one — and the first customer to sign up and
 * find it missing is the one who tells everybody.
 *
 * WHAT CHANGED ON 12 SEPTEMBER 2026, AND WHY IT HAD TO
 * This page used to promise an AI voice that would hold the conversation and
 * transfer at "yes". That product is not being built, and the platform now
 * actively refuses it: `telephony/telnyx.ts` will not send `audio_url`,
 * `media_name`, `stream_url` or any TTS field, a test asserts those never reach
 * the wire, and migration 0026 makes `dial_mode='predictive'` unrepresentable
 * in the database. FCC 24-17 ¶5 treats an AI voice as an "artificial" voice
 * because a person is not speaking it, and DA 20-1507 ¶12 holds that a live
 * operator monitoring a recording does not cure that.
 *
 * So a page advertising it was not merely stale. It was advertising the one
 * thing the code is built to prevent, to an audience of insurance agencies who
 * would be the ones sued for it. What replaced it is what is actually being
 * built: an automated dialer that human agents work, and an intelligence layer
 * that reads every call and decides what happens next.
 *
 * The ambition is not softened anywhere. It is only pointed at the real thing.
 */

export const MARK = `<svg viewBox="0 0 48 48" aria-hidden="true">
  <defs><linearGradient id="lm" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="var(--copper)"/><stop offset="1" stop-color="var(--crit)"/>
  </linearGradient></defs>
  <path d="M36.63 12.63 A17 17 0 1 0 36.63 35.37" fill="none" stroke="url(#lm)" stroke-width="5.4" stroke-linecap="round"/>
  <path d="M29.45 16.22 A9.5 9.5 0 1 0 29.45 31.78" fill="none" stroke="url(#lm)" stroke-width="3.9" stroke-linecap="round"/>
  <circle cx="24" cy="24" r="3.5" fill="url(#lm)"/>
  <circle cx="41.6" cy="24" r="2.3" fill="var(--copper)"/>
  <circle cx="46.4" cy="24" r="1.3" fill="var(--copper)" opacity=".5"/>
</svg>`;

/** The old day against the new one. Left is what an agent lives now. */
const SHIFT: Array<[string, string]> = [
  ['300 dials to find 4 conversations', 'Every conversation is with somebody who already said yes'],
  ['The best hours of the day spent on voicemail', 'The best hours spent in front of families who want cover'],
  ['New agents quit inside 90 days from rejection', 'New agents start on live conversations from week one'],
  ['Income decided by how many numbers you can burn through', 'Income decided by how well you advise'],
];

interface Feature { title: string; body: string; accent: string }

/** Shipped. Everything here is written in the present tense on purpose. */
const LIVE: Feature[] = [
  {
    title: 'Every lead, cleaned on the way in',
    accent: 'var(--aqua)',
    body: 'Drop a file — any size, any number of them — and it arrives usable: numbers standardised and checked against the real numbering plan, names untangled from whatever the vendor called the column, states recovered from a ZIP, dates of birth read whichever way round they were written. Duplicates fold into the record you already had, so nobody works the same person twice.',
  },
  {
    title: 'The list ranked, with its reasoning shown',
    accent: 'var(--copper)',
    body: 'Every lead is scored, and every score opens to show which facts moved it and by how much. Not a number handed down by a black box — a list of reasons an experienced agent can argue with, and change when the model is wrong about your book.',
  },
  {
    title: 'Compliance checked before the dial',
    accent: 'var(--crit)',
    body: 'Every attempt passes a gate first: the calling window in the called party’s own time zone under the stricter of the federal and state rule, internal and national do-not-call, consent where the vendor warranted it, attempt caps, and whether the agent is even licensed in that state. A dial that would break a rule is refused, and the refusal is recorded.',
  },
  {
    title: 'Who sees what, decided in one place',
    accent: 'var(--aqua)',
    /*
     * THIS PARAGRAPH CLAIMED SOMETHING THE SCHEMA DOES NOT DO.
     *
     * It said the agent/team/owner split is "enforced by the database itself".
     * It is not. Row-level security keys on `org_id`: it separates one agency
     * from another, and that part is real and is the part that matters most.
     * The split WITHIN an agency is `LeadService.visibilityFilter`, in
     * application code — and out of the box it narrows nothing, because
     * `sharedLeads` defaults on so a freshly imported book is not invisible to
     * the people who have to work it.
     *
     * Selling a default-off setting as a database guarantee is the worst thing
     * of its kind to get wrong on a landing page: an agency reads it, leaves
     * the setting alone because it sounds already handled, and believes
     * something about its own data that is not true. `views/legal.ts` states
     * this correctly; this is the page that overreached. Say what is enforced
     * where, and say the default out loud.
     */
    body: 'Agencies are separated from each other by the database itself — Postgres row-level security, not a WHERE clause somebody can forget to write. Inside an agency you choose: share the book, or narrow it so an agent sees their own, a team leader sees their team, and an owner sees everything. Sharing is the default, because an agency that has just imported its book and assigned nothing should not open to an empty screen. A compliance officer reads everything and changes only the do-not-call list and the calling rules.',
  },
  {
    title: 'Production and chargebacks that reconcile',
    accent: 'var(--copper)',
    body: 'Applications, issued policies, annualised premium, persistency and chargebacks tracked against the agent and the lead that produced them — so you can finally say what a lead source is worth after the business that fell off, not on the day it was written.',
  },
  {
    title: 'A record that cannot be quietly edited',
    accent: 'var(--crit)',
    body: 'Every consequential action is written to an audit trail where each entry is cryptographically chained to the one before it. Alter a past entry and the chain breaks and the system proves it. When a carrier or a regulator asks what happened, the answer is not somebody’s recollection.',
  },
];

/**
 * The dialer and the layer that reads it.
 *
 * NO STEP HERE DESCRIBES A MACHINE TALKING TO A CONSUMER, and that is the
 * point. A licensed human says every word this system puts in front of a
 * member of the public. What is automated is everything either side of the
 * conversation: who gets called, when, and what the call meant.
 */
const DIALER: Array<{ step: string; title: string; body: string }> = [
  {
    step: 'It decides who to call',
    title: 'The next number, not a list to pick from',
    body: 'Your whole book, ranked continuously — by what the lead is worth, by the hour that lead is most likely to answer, by which of your agents is licensed in that state, and by the calling window where the person actually lives. Numbers that have gone dead fall out on their own. Nobody chooses a name off a screen again.',
  },
  {
    step: 'It places the call',
    title: 'One call at a time, and it rings before they speak',
    body: 'The agent presses nothing between conversations. Strictly one call per agent — no predictive overdialling, no abandoned calls, nothing answering silence. The compliance gate clears every number before it is dialled and refuses rather than warns.',
  },
  {
    step: 'It reads what happened',
    title: 'The conversation writes the record',
    body: 'The call is transcribed and read: what the client agreed to, which carrier, what premium, what was promised and when to ring back. Nothing sensitive survives into storage — no social security number, no bank account, no routing or card number. They are caught by shape and by cue, so a number spoken aloud as words is caught the same as one said as digits.',
  },
  {
    step: 'It learns from every call',
    title: 'What worked, and who to call more of',
    body: 'Every outcome feeds back — which leads convert, which hours answer, which sources are worth buying, and where a deal fell apart. Per agent and across the floor. The list that gets dialled tomorrow is not the list you bought; it is what the calls have taught it.',
  },
];

/**
 * Carriers agencies on Cellvora write.
 *
 * Named high on the front page because "will this work with what I already
 * sell" is the first question an agency owner asks, and the honest answer —
 * any carrier you are appointed with — sounds evasive until you show some.
 *
 * WHAT THIS IS NOT
 * Not a list of integrations, and the page must never imply one. Cellvora does
 * not connect to a carrier's systems: no e-application handoff, no
 * policy-status feed, no commission download. It records the business an agency
 * writes, against whichever carriers it writes it with.
 *
 * WHY NAMES AND NOT LOGOS
 * Every one of these is a registered trademark. A wall of carrier logos on a
 * vendor's home page is read — by visitors and by the carriers' own brand teams
 * — as a partnership or an endorsement, and most of these companies licence
 * their marks in writing or not at all. `logo` is here and unset for exactly
 * that reason: the day written permission arrives for one, drop the file in and
 * that carrier renders as its own mark with no other change.
 *
 * Trade names as an agent would say them, checked against each carrier's own
 * site. Corebridge Financial is the former AIG Life & Retirement.
 */
export interface CarrierBrand {
  name: string;
  /** Path to a licensed logo, once there is written permission to show one. */
  logo?: string;
}

export const CARRIERS: readonly CarrierBrand[] = [
  { name: 'Transamerica' },
  { name: 'Corebridge Financial' },
  { name: 'Mutual of Omaha' },
  { name: 'Aetna' },
  { name: 'Americo' },
  { name: 'Liberty Bankers Life' },
  { name: 'Ethos' },
];

/** Just the names, for anywhere that creates carrier records. */
export const CARRIER_NAMES: readonly string[] = CARRIERS.map((c) => c.name);

const AUDIENCE: Array<{ role: string; line: string }> = [
  { role: 'Agents', line: 'Stop prospecting. Start advising. Your day becomes the conversations you are licensed and paid to have.' },
  { role: 'Team leaders', line: 'See your team’s whole pipeline and activity, reassign work, and know exactly where the business is coming from.' },
  { role: 'Agency owners', line: 'Cost per lead against issued premium, agent production, and a compliance position you can actually evidence.' },
  { role: 'Compliance', line: 'Read everything, change nothing, and export a defensible record of every dial, every disclosure and every consent.' },
];

/**
 * The footer links, in the order they appear.
 *
 * Add a page by adding a line here and a route in main.ts. Two edits, no
 * markup, which is the point — a footer that is awkward to extend becomes a
 * footer that is three pages out of date.
 *
 * Every entry must go somewhere real. A footer link to a page that does not
 * exist is worse than no link: on a page whose whole subject is what a company
 * is and is not responsible for, a dead "Legal" link is the first thing a
 * careful reader notices.
 */
export const FOOTER_LINKS: ReadonlyArray<{ label: string; href: string }> = [
  { label: 'Legal', href: '/legal' },
];

export function landingView(go: (p: string) => void): HTMLElement {
  const page = el('div', { class: 'lp' });

  const start = (label: string): HTMLElement => {
    const b = el('button', { class: 'lp-install', type: 'button' }, el('span', { text: label }));
    b.addEventListener('click', () => go('/sign-up'));
    return b;
  };

  const signIn = el('button', { class: 'btn', type: 'button', text: 'Sign in' });
  signIn.addEventListener('click', () => go('/sign-in'));

  /* ---------------------------------------------------------------- top -- */
  page.append(
    el('header', { class: 'lp-top' },
      el('a', { class: 'lp-brand', href: '#/', 'aria-label': 'Cellvora' },
        el('span', { class: 'lp-mark', html: MARK }),
        el('span', { class: 'lp-word', text: 'CELLVORA' })),
      signIn),
  );

  /* --------------------------------------------------------------- hero -- */
  page.append(
    el('section', { class: 'lp-enter lp-hero' },
      el('p', { class: 'lp-eyebrow', text: 'CRM · quoting · automated dialer — one system' }),
      el('h1', { class: 'lp-h1' },
        'Your agents were never hired to ',
        el('span', { class: 'lp-em', text: 'look for the call.' })),
      el('p', { class: 'lp-line' },
        'They were hired to sit with a family and work out what happens to them if the worst does. '
        + 'Cellvora is one platform built to give them every hour of that back — the CRM your '
        + 'agency runs on today, a quoting engine built into it rather than bolted beside it, and '
        + 'a dialer that decides who to call, when, and hands them the next conversation without '
        + 'anybody choosing a name off a list.'),
      el('div', { class: 'lp-actions' }, start('Create your agency')),
      el('p', { class: 'lp-hint', text: 'Free to start. Set up in an afternoon. Bring your own lead lists, your own carriers and your own rate cards.' }),
      el('p', { class: 'lp-alt' },
        'Already have an account? ',
        (() => {
          const a = el('button', { class: 'lnk', type: 'button', text: 'Sign in' });
          a.addEventListener('click', () => go('/sign-in'));
          return a;
        })(),
        el('span', { class: 'lp-alt2' },
          'Invited by your agency? Open the invitation link they sent you. Already signed up? ',
          (() => {
            const a = el('button', { class: 'lnk', type: 'button', text: 'Install the app' });
            a.addEventListener('click', () => go('/install'));
            return a;
          })())),
    ),
  );

  /* -------------------------------------------------------------- shift -- */
  /* ------------------------------------------------------------ carriers -- */
  /*
   * Directly under the hero, because it answers the first objection before it
   * is raised. The names scroll rather than wrap: seven of them set large
   * enough to read do not fit a phone in one row, and a marquee holds twenty as
   * comfortably as seven. The track is duplicated so the loop has no seam, and
   * the whole thing goes still for anybody who has asked their system for less
   * motion.
   */
  const carrierTrack = (): HTMLElement => {
    const track = el('div', { class: 'lp-cartrack' });
    // Twice through, so the second copy is sliding in as the first slides out.
    for (const pass of [0, 1]) {
      for (const c of CARRIERS) {
        track.append(el('span', {
          class: 'lp-carrier',
          ...(pass === 1 ? { 'aria-hidden': 'true' } : {}),
        }, c.logo
          ? el('img', { src: c.logo, alt: c.name, class: 'lp-carlogo', loading: 'lazy' })
          : c.name));
      }
    }
    return track;
  };

  page.append(
    el('section', { class: 'lp-carriers', 'aria-label': 'Carriers' },
      el('div', { class: 'lp-inner' },
        el('p', { class: 'lp-eyebrow lp-careyebrow', text: 'The carriers you already write' }),
        el('div', { class: 'lp-carmarquee' }, carrierTrack()),
        el('p', { class: 'lp-carline' },
          'Cellvora works with whatever you are appointed with. Set your carriers up on your '
          + 'first afternoon and every case, every producer appointment and every production '
          + 'figure is tracked against them — placement rate by carrier, what each one really '
          + 'pays, and how long each takes to get a case from submitted to placed.'))),
  );

  page.append(
    el('section', { class: 'lp-band' },
      el('div', { class: 'lp-inner' },
        el('h2', { class: 'lp-h2', text: 'What changes' }),
        el('p', { class: 'lp-sub' },
          'This industry has always run on volume. Buy more leads, dial harder, accept that most '
          + 'of it is wasted because nobody can say which part. That was never a strategy. It was '
          + 'the only thing available.'),
        el('div', { class: 'lp-vs' },
          ...SHIFT.map(([before, after]) => el('div', { class: 'lp-vsrow' },
            el('div', { class: 'lp-was' }, el('span', { class: 'lp-vslabel', text: 'Today' }), el('p', { text: before })),
            el('div', { class: 'lp-arrow', html: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h15"/><path d="m13 6 6 6-6 6"/></svg>' }),
            el('div', { class: 'lp-now' }, el('span', { class: 'lp-vslabel', text: 'Cellvora' }), el('p', { text: after }))))),
        el('p', { class: 'lp-punch' },
          'An agent who never has to prospect is an agent who does the job they trained for, all '
          + 'day, every day. That is not a productivity gain. It is a different profession.'))),
  );

  /* ------------------------------------------------- one system, not two -- */
  page.append(
    el('section', { class: 'lp-section lp-onesys' },
      el('h2', { class: 'lp-h2', text: 'One system. Not three you pay for separately.' }),
      el('p', { class: 'lp-sub' },
        'Every agency in this industry runs the same patchwork: a CRM that does not know what the '
        + 'dialer did, a dialer that does not know what the CRM knows, a quoting tool on a '
        + 'separate subscription that knows nothing about either, and a spreadsheet holding all '
        + 'of it together. Those gaps are where the money leaks and where the compliance risk '
        + 'lives. Cellvora closes them by never opening them.'),
      el('div', { class: 'lp-pillars' },
        el('div', { class: 'lp-half' },
          el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
          el('h3', { text: 'The CRM' }),
          el('p', null,
            'Leads cleaned on arrival, scored with their reasoning shown, routed to an agent '
            + 'licensed in that state, gated against every calling rule before a dial is allowed, '
            + 'and tracked through to issued premium and chargeback — all inside a record that '
            + 'cannot be quietly edited.')),
        el('div', { class: 'lp-joint', html: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M7 12h10"/></svg>' }),
        el('div', { class: 'lp-half' },
          el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
          el('h3', { text: 'The quoter' }),
          el('p', null,
            'Five questions on the lead you already have open, and every carrier you are '
            + 'appointed with prices at once — off the rate cards you loaded, ranked cheapest '
            + 'first. No separate login, no separate subscription, and the quote attaches to the '
            + 'lead it came from.')),
        el('div', { class: 'lp-joint', html: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M7 12h10"/></svg>' }),
        el('div', { class: 'lp-half' },
          el('span', { class: 'lp-badge lp-badge-soon', text: 'In development' }),
          el('h3', { text: 'The dialer' }),
          el('p', null,
            'The same lead record, the same compliance gate, the same audit trail — driving a '
            + 'voice system that works your whole book at once and hands your agent a live '
            + 'conversation with somebody who has already said yes.'))),
      el('p', { class: 'lp-punch' },
        'None of these is an integration. It is one platform reading the same records, obeying '
        + 'the same rules and writing to the same trail — which is the only way a quote can know '
        + 'who it is for, and the only way an automated call can be as compliant as a careful '
        + 'human one.')),
  );

  /* ------------------------------------------------------- live already -- */
  page.append(
    el('section', { class: 'lp-section' },
      el('div', { class: 'lp-sechead' },
        el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
        el('h2', { class: 'lp-h2', text: 'The system your agency runs on' })),
      el('p', { class: 'lp-sub' },
        'Before anything can be automated it has to be true. One system covering the whole path a '
        + 'lead travels — from the file it arrived in, through scoring and routing and the '
        + 'compliance gate, to the policy it becomes and the chargeback it might yet be.'),
      el('div', { class: 'lp-grid' },
        ...LIVE.map((f) => el('article', { class: 'lp-card', style: `--accent:${f.accent}` },
          el('h3', { text: f.title }),
          el('p', { text: f.body }))))),
  );

  /* -------------------------------------------------------------- quoter -- */
  /*
   * Present tense throughout, and it earns it: the quoting engine ships and is
   * covered by the test suite. The one thing this section must never imply is a
   * live carrier rate feed. Cellvora prices from cards the agency loads itself,
   * and that limitation is the section's strongest argument rather than a
   * caveat to bury — every tool in this market shows a number whether or not it
   * holds a current card, and agents have been quoting stale rates for years
   * because of it.
   */
  page.append(
    el('section', { class: 'lp-band' },
      el('div', { class: 'lp-inner' },
        el('div', { class: 'lp-sechead' },
          el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
          el('h2', { class: 'lp-h2', text: 'And it quotes. Off your cards, not a guess.' })),
        el('p', { class: 'lp-sub' },
          'Quoting has always been a separate subscription, a separate login and a separate set '
          + 'of client details to retype. Here it is a tab on the lead you already have open. '
          + 'First name, last name, age, sex, smoker — and every carrier you are appointed with '
          + 'prices at once, ranked cheapest first, with how far behind the leader each one came.'),
        el('div', { class: 'lp-fit' },
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'Nothing is estimated' }),
            el('p', null, 'Every premium comes off a rate card your agency loaded from your own '
              + 'carrier. Nothing is interpolated between ages, between face amounts or between '
              + 'term lengths. A product with no card loaded says so plainly and sends you to '
              + 'that carrier’s own tool — because an agent cannot tell an invented premium from '
              + 'a real one, and the family finds out at issue.')),
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'Only what the carrier actually writes' }),
            el('p', null, 'Whole life, term life and indexed universal life. A carrier that does '
              + 'not write one of them says so in those words instead of returning an empty '
              + 'screen, so a new agent learns your panel by using it rather than by being '
              + 'corrected in front of a client.')),
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'It tells you when your card is stale' }),
            el('p', null, 'Type in what a carrier’s own tool just returned and Cellvora compares '
              + 'it to your card. If they disagree it flags the card for reloading. Only a figure '
              + 'a person actually entered counts as evidence — our card agreeing with itself '
              + 'proves nothing.'))),
        el('div', { class: 'lp-note' },
          el('b', null, 'Why this one is free. '),
          'There is no rating API behind it and no subscription funding one. You load the cards '
          + 'your carriers already send you, and the engine does the arithmetic. That is the '
          + 'whole trade: a little setup on your first afternoon, and no monthly bill for the '
          + 'privilege of quoting your own products.'))),
  );

  /* --------------------------------------------------------- draft dates -- */
  /*
   * Present tense, and it earns it: packages/core/src/benefits.ts ships,
   * migration 0011 carries the two columns, and the board panel is live.
   *
   * WHAT THIS SECTION MAY NOT SAY, ever:
   *   - that it PREVENTS a lapse. It tells an agent which policies are set to
   *     draft before the client is paid. A person still has to ring the carrier
   *     and move the date. Claiming the outcome rather than the mechanism is
   *     how a true sentence quietly becomes a false one.
   *   - that it changes a draft date, talks to a carrier, or files anything.
   *     There is no carrier integration behind this and none coming soon.
   *   - that it knows when a particular person was actually paid. It knows the
   *     SSA's published rule and the date of birth on the application. It is
   *     arithmetic, not a bank feed, and the page says so in those words.
   *   - a comparative claim, however tempting. The mechanism is unusual enough
   *     stated plainly.
   *
   * The lapse figures are Gottlieb & Smetters, American Economic Review 111(8),
   * 2021. They describe the industry rather than this product, and they are
   * attributed on the page instead of being passed off as our own measurement.
   */
  page.append(
    el('section', { class: 'lp-band' },
      el('div', { class: 'lp-inner' },
        el('div', { class: 'lp-sechead' },
          el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
          el('h2', { class: 'lp-h2', text: 'It knows when your client actually gets paid' })),
        el('p', { class: 'lp-sub' },
          'Most policies that die were never cancelled. The premium simply came out before the '
          + 'money went in, the draft bounced, and a policy that was written correctly, priced '
          + 'correctly and sold honestly lapsed anyway — with the agent charged back for it.'),
        el('div', { class: 'lp-fit' },
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'The date of birth is already on the application' }),
            el('p', null, 'Social Security pays on a published rule set by the day of the month '
              + 'the beneficiary was born — the second Wednesday, the third, or the fourth. Ask a '
              + 'client once how their money arrives, and Cellvora works out the days a premium '
              + 'can safely be drafted for the rest of that policy’s life.')),
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'The rule of thumb is wrong for a third of your book' }),
            el('p', null, '“Avoid early in the month” is right for somebody born on the 5th and '
              + 'exactly wrong for somebody born on the 25th, whose deposit lands between the '
              + '22nd and the 28th — their worst possible draft day is the 18th, not the 1st. '
              + 'One heuristic applied to everybody puts some drafts on the single worst day '
              + 'available to that client.')),
          el('div', { class: 'lp-fitcard' },
            el('h3', { text: 'Asked across the whole book at once' }),
            el('p', null, 'The cases board lists every policy set to draft before its client is '
              + 'paid, with the days that would be safe instead. Moving one is a phone call to '
              + 'the carrier: Cellvora finds them and tells you which days to ask for. It does '
              + 'not change anything at the carrier itself.'))),
        el('div', { class: 'lp-note' },
          el('b', null, 'Where those numbers come from. '),
          'Roughly seven in ten life policy lapses are non-payment rather than a decision to '
          + 'cancel, and the peer-reviewed work on why policies die attributes 37.8% to '
          + 'forgetfulness against 15.4% to an income shock (Gottlieb & Smetters, American '
          + 'Economic Review 111(8), 2021). Those figures describe the industry, not this '
          + 'software. What Cellvora does is arithmetic on a date of birth you already hold — '
          + 'it reads no bank account and talks to no carrier.'))),
  );

  /* ------------------------------------------------------- what it refuses -- */
  /*
   * The differentiation section, and the one most likely to drift into a claim
   * we cannot stand behind. Every line here describes what CELLVORA does. None
   * of them says "no other system does this", even where the research supports
   * it, because a comparative claim on a public page has to stay true for as
   * long as the page is up, and a competitor shipping the same feature next
   * quarter would make it false without anybody here noticing.
   *
   * The contrast lands anyway. Enforcing a rule rather than documenting it is
   * self-evidently different to anyone who has used the alternatives.
   */
  page.append(
    el('section', { class: 'lp-section' },
      el('h2', { class: 'lp-h2', text: 'Four things it refuses to do' }),
      el('p', { class: 'lp-sub' },
        'Most software in this industry is built to say yes — record the rule, show the number, '
        + 'let the agent decide. Every one of these refusals costs a feature demo and prevents a '
        + 'loss that is far more expensive than the demo.'),
      el('div', { class: 'lp-grid lp-grid-2up' },
        el('article', { class: 'lp-card', style: '--accent:var(--crit)' },
          el('h3', { text: 'It will not connect a call that breaks a rule' }),
          el('p', { text: 'The calling window is computed for the person being called — their '
            + 'state, their time zone, the stricter of the federal and state rule — and the dial '
            + 'is refused, not flagged. A do-not-call hit, a missing consent, a stale scrub or an '
            + 'attempt cap stops it the same way. The refusal is written down with its reason.' })),
        el('article', { class: 'lp-card', style: '--accent:var(--copper)' },
          el('h3', { text: 'It will not route a lead to an unlicensed producer' }),
          el('p', { text: 'Licence status by state is a condition of receiving the lead, not a '
            + 'document filed somewhere for an audit. An agent without a licence in that state '
            + 'cannot be assigned the record, cannot open it, and cannot be handed it by any '
            + 'screen, export or API call.' })),
        el('article', { class: 'lp-card', style: '--accent:var(--aqua)' },
          el('h3', { text: 'It will not show a premium it cannot source' }),
          el('p', { text: 'No rate card, no number. It would be trivial to interpolate something '
            + 'plausible and every tool in this market does. A premium nobody will honour is an '
            + 'errors-and-omissions claim waiting to happen and a family told the wrong thing '
            + 'about what they can afford.' })),
        el('article', { class: 'lp-card', style: '--accent:var(--crit)' },
          el('h3', { text: 'It will not let the record be quietly changed' }),
          el('p', { text: 'Every consequential action is chained cryptographically to the one '
            + 'before it. Alter a past entry and the chain breaks and the system says so — '
            + 'including for an administrator, including for us. When a carrier or a regulator '
            + 'asks what happened, the answer is evidence rather than recollection.' }))),
      el('p', { class: 'lp-punch' },
        'Software that cannot say no is software that leaves the hard decisions to whoever is '
        + 'most tired at four in the afternoon.')),
  );

  /* ------------------------------------------------------------- dialer -- */
  page.append(
    el('section', { class: 'lp-band lp-dialerband' },
      el('div', { class: 'lp-inner' },
        el('div', { class: 'lp-sechead' },
          el('span', { class: 'lp-badge lp-badge-soon', text: 'In development' }),
          el('h2', { class: 'lp-h2', text: 'And then the list stops being your problem' })),
        el('p', { class: 'lp-sub' },
          'No more choosing who to ring. No more dead numbers worked for a month before anybody '
          + 'notices. No more writing up a call after it ends. The dialer decides who is next, '
          + 'places the call, and reads the conversation afterwards so the record writes itself — '
          + 'and every outcome goes back into deciding who gets called tomorrow.'),
        el('div', { class: 'lp-flow' },
          ...DIALER.map((d, i) => el('article', { class: 'lp-flowstep' },
            el('span', { class: 'lp-stepnum', text: String(i + 1).padStart(2, '0') }),
            el('span', { class: 'lp-steplabel', text: d.step }),
            el('h3', { text: d.title }),
            el('p', { text: d.body })))),
        el('div', { class: 'lp-note' },
          el('b', null, 'Where it stands. '),
          el('span', null,
            'The dialer is in development and is not part of the platform today — everything in '
            + 'the section above this one is, and is what you get when you sign up now. We are '
            + 'saying plainly what is built and what is coming, because an agency that finds out '
            + 'the difference after signing up is an agency we deserve to lose.'),
          el('br', null), el('br', null),
          el('b', null, 'No machine speaks to your client. '),
          el('span', null,
            'Every word a member of the public hears is spoken live by a licensed person, and the '
            + 'platform is built to enforce that rather than trust it. An artificial voice on an '
            + 'outbound call is treated by the FCC as a recorded call whether or not a human is '
            + 'listening in, and the liability for that lands on the agency, not on its software. '
            + 'So the automation sits either side of the conversation: it decides who to call, '
            + 'and it understands what was said. The conversation itself is your agent — which is '
            + 'the part of this job that should never have been automated anyway.')))),
  );

  /* ---------------------------------------------------------------- ai -- */
  page.append(
    el('section', { class: 'lp-section' },
      el('div', { class: 'lp-sechead' },
        el('span', { class: 'lp-badge lp-badge-live', text: 'Available now' }),
        el('h2', { class: 'lp-h2', text: 'Intelligence where it already helps' })),
      el('p', { class: 'lp-sub' },
        'Waiting for the dialer does not mean waiting for the benefit. Today the assistant takes '
        + 'the reading and the writing off your agents so their attention goes where it earns.'),
      el('div', { class: 'lp-ai' },
        el('div', { class: 'lp-aicard' },
          el('h3', { text: 'It reads the record so they do not have to' }),
          el('p', null, 'Before a call, a summary of what matters about this person and this '
            + 'household — what they asked for, what has been tried, what changed. The agent '
            + 'opens the lead already knowing the situation.')),
        el('div', { class: 'lp-aicard' },
          el('h3', { text: 'It explains the ranking in words' }),
          el('p', null, 'Why is this lead above that one? Written out, from the same factors '
            + 'that produced the score — so a new agent learns the book’s logic instead of '
            + 'following a number on faith.')),
        el('div', { class: 'lp-aicard' },
          el('h3', { text: 'It writes the follow-up' }),
          el('p', null, 'The message after a missed call, the note after a conversation, the '
            + 'summary for a team leader. Drafted in seconds, edited and sent by the person who '
            + 'owns the relationship.'))),
      el('div', { class: 'lp-note' },
        el('b', null, 'Your customers’ details never leave your system to be processed by a model. '),
        'Phone numbers, email addresses, street addresses and bank details are stripped before '
        + 'anything is sent, and a date of birth becomes an age. That is enforced in one place in '
        + 'the code that everything else has to go through.')),
  );

  /* ------------------------------------------------------------ for who -- */
  page.append(
    el('section', { class: 'lp-band' },
      el('div', { class: 'lp-inner' },
        el('h2', { class: 'lp-h2', text: 'One account, whatever your job is' }),
        el('p', { class: 'lp-sub' },
          'Everybody signs in at the same place. What you can see and do is decided by your '
          + 'account, not by finding a different login.'),
        el('div', { class: 'lp-roles' },
          ...AUDIENCE.map((a) => el('div', { class: 'lp-role' },
            el('h4', { text: a.role }),
            el('p', { text: a.line })))))),
  );

  /* --------------------------------------------------------------- fits -- */
  page.append(
    el('section', { class: 'lp-section' },
      el('h2', { class: 'lp-h2', text: 'It fits the agency you already have' }),
      el('div', { class: 'lp-fit' },
        el('div', { class: 'lp-fitcard' },
          el('h3', { text: 'Any carrier lineup' }),
          el('p', null, 'Carrier-agnostic. You record the carriers and products you actually '
            + 'write and it tracks production against them. It does not tie you to a panel and '
            + 'does not care whose paper you place.')),
        el('div', { class: 'lp-fitcard' },
          el('h3', { text: 'Any lead source' }),
          el('p', null, 'Vendor lists, your own web forms, aged data, referrals, live transfers '
            + '— all of it arrives as a file and is cleaned the same way, with cost per lead '
            + 'tracked per source so you can see which are worth buying again.')),
        el('div', { class: 'lp-fitcard' },
          el('h3', { text: 'Any agency shape' }),
          el('p', null, 'Two people at one desk or two hundred across nine states, captive or '
            + 'independent, flat or in teams under leaders. The visibility rules bend to the '
            + 'shape you have.'))),
      el('p', { class: 'lp-note' },
        el('b', null, 'What it is not. '),
        'Cellvora does not sell you leads, does not bind coverage, and is not a substitute for '
        + 'your compliance counsel. It quotes only from the rate cards your agency loads — there '
        + 'is no live carrier rate feed behind it, and it never estimates a premium it cannot '
        + 'source. You bring the licences, the carriers, the cards and the book.')),
  );

  /* --------------------------------------------------------------- close -- */
  page.append(
    el('section', { class: 'lp-close' },
      el('h2', { class: 'lp-h2', text: 'Start with one list.' }),
      el('p', null,
        'Create your agency, drop in a lead file, and watch it come back cleaned, ranked and '
        + 'checked against every rule that governs the call. Paste in one rate card and quote off '
        + 'it the same afternoon. Run your whole book on it. Then be standing on the right '
        + 'foundation on the day the phone stops being your problem — because the dialer is being '
        + 'built into the system you are already running.'),
      el('div', { class: 'lp-actions' }, start('Create your agency'))),
  );

  /* --------------------------------------------------------------- foot -- */
  page.append(
    el('footer', { class: 'lp-foot' },
      el('span', { html: MARK, class: 'lp-footmark' }),

      /*
       * The footer links.
       *
       * One row, wrapping on a narrow screen, and adding another is one entry
       * in FOOTER_LINKS — nothing else changes. Kept as data rather than as
       * markup because this list will grow and a list that is painful to grow
       * ends up as a stale footer nobody maintains.
       *
       * Buttons rather than <a href>: this is a hash-routed single page, and a
       * real href reloads the whole app to serve a route the router would have
       * handled instantly. The class keeps them looking like links, and the
       * keyboard treats a button as a button, which is correct for something
       * that navigates within the page.
       */
      el('nav', { class: 'lp-footnav', 'aria-label': 'Footer' },
        ...FOOTER_LINKS.map((link) => {
          const b = el('button', { class: 'lp-footlink', type: 'button', text: link.label });
          b.addEventListener('click', () => go(link.href));
          return b;
        })),

      el('p', { class: 'lp-legal' },
        'Cellvora is agency software. It is not an insurer, not an agency, and not a source of '
        + 'leads, and it gives no legal, compliance or tax advice. Calling rules, licensing, '
        + 'suitability and record-keeping remain the responsibility of the agency using it. '
        + 'Features described as in development are not available today and no date is promised '
        + 'for them.'),
      el('p', { class: 'lp-copy', text: `© ${new Date().getFullYear()} Cellvora` })),
  );

  return page;
}
